/**
 * @fileoverview Password-policy API call.
 * @description The one function that calls GET /identity/password-policy, the real backend
 * endpoint describing the platform's password complexity rules. Used by the set-password form's
 * live rule checklist so it can never drift from what accept-invitation actually enforces
 * server-side.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { httpClient } from '@/shared/api/http-client'

export type PasswordPolicyRuleCode =
  | 'MIN_LENGTH'
  | 'MAX_LENGTH'
  | 'UPPERCASE'
  | 'LOWERCASE'
  | 'NUMBER'
  | 'SPECIAL'
  | 'NO_EMAIL'
  | 'NO_NAME'
  | 'PASSWORD_HISTORY'

/** GET /identity/password-policy (PasswordPolicyResponseDto). */
export interface PasswordPolicy {
  policyVersion: string
  minLength: number
  maxLength: number
  requireUppercase: boolean
  requireLowercase: boolean
  requireNumber: boolean
  requireSpecial: boolean
  /** The password may not contain the account's email address — checked server-side only. */
  disallowEmail: boolean
  /** The password may not contain the account holder's name — checked server-side only. */
  disallowName: boolean
  /** How many previous passwords a new one may not repeat — checked server-side only. */
  previousPasswordsChecked: number
  ruleCodes: PasswordPolicyRuleCode[]
}

/**
 * @description Fetches the platform's password complexity policy.
 * @returns {Promise<PasswordPolicy>} The complexity rules, as flags and as rule codes.
 */
export async function getPasswordPolicy(): Promise<PasswordPolicy> {
  const { data } = await httpClient.get<PasswordPolicy>('/identity/password-policy')
  return data
}
